<template>
  <main v-if="course" class="flex flex-col gap-5">
    <Hero
      :course="course"
      v-motion
      :initial="initialMotion"
      :enter="enterMotion"
    />
    <section class="container mx-auto my-5">
      <div
        id="description"
        class="space-y-4 sm:p-0 p-4"
        v-motion
        :initial="initialMotion"
        :enter="enterMotion.getEnterMotion(100)"
      >
        <h5>
          {{
            $t("labels.generic", {
              pt: "Resumo do curso",
              en: "Summary from course",
            })
          }}
        </h5>
        <div
          class="bg-white shadow-md p-6 rounded-md border-b-4 border-h-primary-500"
        >
          <p>
            <span class="font-bold">
              {{
                $t("labels.generic", { pt: "Duração: ", en: "Duration: " })
              }}
            </span>
            {{ generics.getDurationTime(Number(course.duration)) }}
          </p>
          <p class="text-justify mt-4">
            {{ course.description }}
          </p>
        </div>
      </div>
      <div
        v-if="course?.summary"
        id="summary"
        class="space-y-4 sm:p-0 p-4 mt-5"
        v-motion
        :initial="initialMotion"
        :enter="enterMotion.getEnterMotion(200)"
      >
        <h5>
          {{
            $t("labels.generic", {
              pt: "Ementa do curso",
              en: "Course program",
            })
          }}
        </h5>
        <div
          class="bg-white shadow-md p-6 rounded-md border-b-4 border-h-primary-500"
          v-motion
          :initial="initialMotion"
          :enter="enterMotion.getEnterMotion(300)"
        >
          <p class="text-justify" v-html="course?.summary"></p>
        </div>
      </div>

      <div
        id="teacher-profile"
        v-motion
        :initial="initialMotion"
        :enter="enterMotion.getEnterMotion(400)"
        class="mt-10 bg-white shadow-md p-6 rounded-md border-b-4 border-h-primary-500"
      >
        <div class="flex flex-row space-x-4">
          <img
            :src="course.teacher.photoProfile?.path || emptyImg"
            @error="imgDefaultError"
            alt="profile-teacher"
            class="w-20 h-fit bg-center bg-cover rounded-md"
          />
          <div class="space-y-2">
            <p class="text-gray-400 text-[12px]">
              {{
                $t("labels.generic", { pt: "Instrutor(a)", en: "Instructor" })
              }}
            </p>
            <h5>
              {{ course.teacher?.name }}
            </h5>
            <p
              v-if="course.teacher?.teacherProfile?.title"
              class="text-gray-400 text-[12px]"
            >
              {{ course.teacher?.teacherProfile?.title }}
            </p>
          </div>
        </div>
        <div v-if="course.teacher?.teacherProfile" class="mt-8">
          <div v-html="course.teacher?.teacherProfile?.description"></div>
          <div class="flex mt-8 justify-start items-start gap-3">
            <button
              v-if="course.teacher?.teacherProfile?.generalUrl"
              class="border border-h-primary-500 bg-h-primary-100 p-1 rounded-md hover:scale-95 transition-all"
              :disabled="true"
            >
              <a
                :href="course.teacher?.teacherProfile?.generalUrl"
                target="_blank"
                v-tooltip.bottom="'Site'"
              >
                <v-icon name="hi-globe-alt" class="text-primary-400" />
              </a>
            </button>
            <button
              v-if="course.teacher?.teacherProfile?.lattesUrl"
              class="border border-h-primary-500 bg-h-primary-100 p-1 rounded-md hover:scale-95 transition-all"
              :disabled="true"
            >
              <a
                :href="course.teacher?.teacherProfile?.lattesUrl"
                target="_blank"
                v-tooltip.bottom="'Lattes'"
              >
                <v-icon name="ai-lattes" class="text-primary-400" />
              </a>
            </button>
            <button
              v-if="course.teacher?.teacherProfile?.linkedinUrl"
              class="border border-h-primary-500 bg-h-primary-100 p-1 rounded-md hover:scale-95 transition-all"
              :disabled="true"
            >
              <a
                :href="course.teacher?.teacherProfile?.linkedinUrl"
                target="_blank"
                v-tooltip.bottom="'Linkedin'"
              >
                <v-icon name="co-linkedin-in" class="text-primary-400" />
              </a>
            </button>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { Course } from "@/models/courses.model";
import {
  MotionEnter,
  MotionInitial,
  MotionTransition,
} from "@/models/motion.model";
import { User } from "@/models/user.model";
import HandleError from "@/utils/handle-errors.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { defineAsyncComponent, defineComponent } from "vue";
import { CourseStudentService } from "../course-student.service";
import { GenericsUtils } from "@/utils/generics.util";

const Hero = defineAsyncComponent(() => import("./Hero/hero-course.vue"));

export default defineComponent({
  name: "course-information",
  components: { Hero },
  data() {
    return {
      course: new Course(),
      teacher: {} as User,
      emptyImg:
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png" as string,
      initialMotion: new MotionInitial(),
      enterMotion: new MotionEnter(1, 0, 0, new MotionTransition(40)),
    };
  },
  mounted() {
    this.getCourseById();
  },
  computed: {
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    service(): CourseStudentService {
      return new CourseStudentService();
    },
    errors(): HandleError {
      return new HandleError();
    },
  },
  methods: {
    getCourseById(): void {
      const userId: string = getUserFromStorage()?.id;
      const courseId: any = this.$route.params.id;
      this.service.course.subscribe({
        next: (response) => {
          this.course = response;
        },
      });
      this.service.getCourseById(userId, courseId);
    },
    imgDefaultError(e: any): void {
      e.target.src =
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png";
    },
  },
});
</script>
